import React, { useEffect, useRef, useState } from 'react'
import { Surface } from '@clickguard/design-system'
import '../form-spam-page.css'

/**
 * The page frame every Form Spam route shares: a canvas surface, a sticky
 * header band, and a padded content column. It exists so the three routes
 * cannot drift apart.
 *
 * The header hairline follows the Custom Rules page: invisible at rest, it
 * fades in only while the header is stuck. The sentinel sits one pixel above
 * the header, so it leaves the scrollport on the exact frame the header pins.
 */
export const FormSpamShell = ({
	header,
	children
}: {
	header: React.ReactNode
	children: React.ReactNode
}) => {
	const sentinelRef = useRef<HTMLDivElement>(null)
	const [isStuck, setIsStuck] = useState(false)

	useEffect(() => {
		const sentinel = sentinelRef.current
		if (!sentinel) return
		const observer = new IntersectionObserver(
			([entry]) => setIsStuck(!entry.isIntersecting),
			{ threshold: 0 }
		)
		observer.observe(sentinel)
		return () => observer.disconnect()
	}, [])

	return (
		<Surface
			/* R149 (Esteban, 28 Aug): the page painted `canvas` (#f6f6f6), the colour
			   of the app's OUTER frame, over the app's content pane (`main-layout.tsx`
			   paints the pane #fafafa). Every other page sits on the pane, so this one
			   read a tone darker. The DS token for the pane is `surface-subtle`
			   (250 250 250), and `Surface` has no `bg` variant for it (`secondary` is
			   `surface-2`, 242 242 242, darker still). An inline style paints the
			   token: the DS bg utility class loads after the page stylesheet and
			   beats a plain `.form-spam-page` rule, so the `bg` prop is only the
			   class the inline style overrides. R166 moved the value behind
				   --fs-page-surface so the dark theme can paint the page tier
				   (#151326) without a second inline branch. */
			bg="primary"
			radius="none"
			bordered={false}
			className="form-spam-page"
			style={{ background: 'var(--fs-page-surface)' }}
		>
			<div ref={sentinelRef} aria-hidden="true" className="h-px -mb-px" />
			<div
				className={`form-spam-page__sticky-header${
					isStuck ? ' form-spam-page__sticky-header--stuck' : ''
				}`}
			>
				{header}
			</div>
			<div className="form-spam-page__content">{children}</div>
		</Surface>
	)
}
